// 관리자 콘솔 웹 푸시 구독 클라이언트.
// 관리자 앱은 PWA 가 아니라 SW 가 자동 등록되지 않으므로, 푸시용 최소 SW(sw.js)를 직접 등록한 뒤 구독한다.
//  - 신규 주문 알림 수신용. VAPID 공개키는 서버(/push/vapid-public-key)에서 받는다.

import { api } from "@/api/client.ts";

// 브라우저가 웹 푸시(SW + PushManager + Notification)를 지원하는지.
export function isPushSupported(): boolean {
  return (
    typeof navigator !== "undefined" &&
    "serviceWorker" in navigator &&
    typeof window !== "undefined" &&
    "PushManager" in window &&
    "Notification" in window
  );
}

// 현재 알림 권한 상태 (미지원 환경은 'unsupported').
export function getNotificationPermission(): NotificationPermission | "unsupported" {
  if (!isPushSupported()) {
    return "unsupported";
  }
  return Notification.permission;
}

function urlBase64ToUint8Array(base64url: string): Uint8Array {
  const padding = "=".repeat((4 - (base64url.length % 4)) % 4);
  const base64 = (base64url + padding).replace(/-/gu, "+").replace(/_/gu, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) {
    out[i] = raw.charCodeAt(i);
  }
  return out;
}

async function fetchVapidPublicKey(): Promise<string> {
  const data = await api.get<{ publicKey: string }>("/push/vapid-public-key", {
    withAuth: false,
  });
  return data.publicKey;
}

// SW 등록을 보장한 뒤 ready 등록을 반환한다.
//  - 앱이 PWA(vite-plugin-pwa)가 되어 SW(sw.js, push 핸들러 포함)가 자동 등록되므로 보통 기존 등록을 그대로 쓴다.
//  - 아직 등록 전이면 절대 경로(/sw.js)로 등록한다(딥 라우트에서도 안전).
async function ensurePushServiceWorker(): Promise<ServiceWorkerRegistration> {
  const existing = await navigator.serviceWorker.getRegistration();
  if (existing) {
    return navigator.serviceWorker.ready;
  }
  await navigator.serviceWorker.register("/sw.js");
  return navigator.serviceWorker.ready;
}

export type SubscribeResult =
  | { ok: true }
  | { ok: false; reason: "unsupported" | "denied" | "not-configured" | "error" };

// 관리자 푸시 구독 + 서버 등록(/admin/push/subscribe).
export async function subscribeAdminPush(): Promise<SubscribeResult> {
  if (!isPushSupported()) {
    return { ok: false, reason: "unsupported" };
  }
  const publicKey = await fetchVapidPublicKey();
  if (!publicKey) {
    return { ok: false, reason: "not-configured" };
  }
  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    return { ok: false, reason: "denied" };
  }
  try {
    const reg = await ensurePushServiceWorker();
    const existing = await reg.pushManager.getSubscription();
    const sub =
      existing ??
      (await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey) as unknown as BufferSource,
      }));
    const json = sub.toJSON();
    await api.post("/admin/push/subscribe", {
      endpoint: json.endpoint,
      keys: { p256dh: json.keys?.p256dh, auth: json.keys?.auth },
      userAgent: navigator.userAgent,
    });
    return { ok: true };
  } catch {
    return { ok: false, reason: "error" };
  }
}

// 관리자 푸시 구독 해제.
export async function unsubscribeAdminPush(): Promise<void> {
  if (!isPushSupported()) {
    return;
  }
  const reg = await navigator.serviceWorker.getRegistration();
  if (!reg) {
    return;
  }
  const sub = await reg.pushManager.getSubscription();
  if (!sub) {
    return;
  }
  const endpoint = sub.endpoint;
  try {
    await api.post("/admin/push/unsubscribe", { endpoint });
  } finally {
    await sub.unsubscribe();
  }
}

// 이 브라우저가 현재 푸시 구독 중인지.
export async function isCurrentlySubscribed(): Promise<boolean> {
  if (!isPushSupported()) {
    return false;
  }
  try {
    const reg = await navigator.serviceWorker.getRegistration();
    if (!reg) {
      return false;
    }
    const sub = await reg.pushManager.getSubscription();
    return sub !== null;
  } catch {
    return false;
  }
}
